/**
 * Flow Optimizer — group-weight normalizer (single source of truth)
 * For Media Devoted | Created By Ciro Autiero
 *
 * Group weights are SHARES of a layer's score: they must ALWAYS sum to exactly
 * 100 — never 99, never 101. This is the ONE canonical implementation, used by
 * both the backend score engine (normalize-group-weights.ts) and the frontend
 * slider editor (auto-balanced-sliders.math.ts), so the weight a user sees and
 * the weight the optimizer scores with can never disagree.
 *
 * It normalizes to proportional shares, then uses largest-remainder (Hamilton)
 * rounding so the integers sum to exactly 100. An all-zero / empty input falls
 * back to an equal split (also summing to 100).
 */

export function normalizeWeightsToHundred(
  weightsById: Readonly<Record<string, number>>,
  ids: ReadonlyArray<string>,
): Record<string, number> {
  if (ids.length === 0) return {};
  if (ids.length === 1) return { [ids[0]]: 100 };
  const raws = ids.map((id) => nonNegative(weightsById[id]));
  const total = raws.reduce((sum, v) => sum + v, 0);
  const shares =
    total > 0
      ? raws.map((v) => (v / total) * 100)
      : ids.map(() => 100 / ids.length);
  const integers = largestRemainderToHundred(shares);
  return Object.fromEntries(ids.map((id, i) => [id, integers[i]]));
}

function nonNegative(value: number | undefined): number {
  return Number.isFinite(value) && (value as number) > 0
    ? (value as number)
    : 0;
}

/**
 * Floor every share, then hand the leftover whole units to the largest
 * fractional remainders (and drain any overflow), so the result sums to
 * exactly 100. Ties break by index for a deterministic, reproducible result.
 */
function largestRemainderToHundred(shares: number[]): number[] {
  const floors = shares.map((s) => Math.floor(s));
  const result = floors.slice();
  const order = shares
    .map((s, i) => ({ i, frac: s - floors[i] }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  let allocated = floors.reduce((sum, v) => sum + v, 0);
  let head = 0;
  while (allocated < 100 && head < order.length) {
    result[order[head++].i]++;
    allocated++;
  }
  let tail = order.length - 1;
  while (allocated > 100 && tail >= 0) {
    const idx = order[tail--].i;
    if (result[idx] > 0) {
      result[idx]--;
      allocated--;
    }
  }
  return result;
}
